<template lang="pug">
ValidationObserver(
  ref="dialogObserver"
  v-slot="{ valid, fields }"
  slim
)
  AppDialog.job-site-modal(
    show
    append-to-body
    :title="editing ? 'Edit job site' : 'New job site'"
    @close="onCloseWizard"
  )
    AppWizard(
      :steps="allSteps"
      :editing="editing"
      :value="currentStep"
      @next="onNavigateToNext"
      @tab-selected="onChangeTab"
      @previous="onNavigateToPrevious"
    )
      NewJobSiteWizardBaseStep(
        v-show="showBaseStep"
        :key="`base-step-${forcedCleanup}`"
        v-model="wizardJobSite"
        :editing="editing"
        :active="showBaseStep"
        :is-last-step="editing ? true : currentStep === 1"
      )
      NewJobSiteWizardRulesStep(
        v-show="showRulesStep"
        :key="`rules-step-${forcedCleanup}`"
        v-model="wizardJobSite"
        :editing="editing"
        :active="showRulesStep"
      )

    //- Having the footer decoupled from the actual wizard steps, makes
    //- it easier to keep the main navigation and actions centralized.
    template(slot="footer")
      //- Creating footer
      .d-flex.flex-column.flex-sm-row.justify-content-between.flex-1
        template(v-if="!editing")
          button.btn.btn-default.mb-15.mb-sm-0(
            key="cancel"
            :disabled="loading"
            @click="onCloseWizard"
          ) Cancel
          div(v-if="currentStep === 0")
            button.btn.btn-primary(
              key="next"
              :disabled="nextButtonDisabled(fields) || loading"
              @click="onNavigateToNext"
            ) Next
          div(v-else)
            button.btn.btn-default(
              key="back"
              @click="onNavigateToPrevious"
            ) Back
            button.btn.btn-primary(
              key="save"
              :disabled="!valid || loading"
              @click="onSaveJobSite"
            ) Save
        //- Editing footer
        template(v-else)
          button.btn.btn-danger.mb-30.mb-sm-0(
            :disabled="loading"
            @click="onDeleteJobSite"
          ) Delete job site
          div
            button.btn.btn-default.pull-left(@click="$emit('close', false)") Cancel
            button.btn.btn-primary(
              :disabled="!valid || loading"
              @click="onSaveJobSite"
            ) Save
</template>

<script>
import { mapActions } from 'vuex';
import { extend } from 'vee-validate';
import veeValidations from '~/vee-validations';
import { cloneDeep, isObject } from 'lodash-es';
import NewJobSiteWizardBaseStep from './NewJobSiteWizardBaseStep.vue';
import NewJobSiteWizardRulesStep from './NewJobSiteWizardRulesStep.vue';

// Rule for the viewers validation
extend('notIn', { ...veeValidations.notIn });

/**
 * TODO: Follow-up task:
 *   - Refactor `TheJobSites.vue` to use the new GoogleMaps mixin methods
 *     and simplify the component.
 *
 * .   Bernat Jufré 2022-03-11
 */
export default {
  name: 'NewJobSiteWizard',
  components: {
    NewJobSiteWizardBaseStep,
    NewJobSiteWizardRulesStep,
  },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    jobSite: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentStep: 0,
      loading: false,
      forcedCleanup: 0,
      wizardJobSite: cloneDeep(this.jobSite),
    };
  },
  computed: {
    allSteps() {
      if (this.editing) {
        return [{ title: 'MEMBERS & SETTINGS' }, { title: 'LOCATION' }];
      }

      return [{ title: 'Add job location', label: 'Create job site' }, { title: 'CONFIGURE SETTINGS' }];
    },
    showBaseStep() {
      return this.editing ? this.currentStep === 1 : this.currentStep === 0;
    },
    showRulesStep() {
      return this.editing ? this.currentStep === 0 : this.currentStep === 1;
    },
  },
  methods: {
    ...mapActions(['createJobSite', 'updateJobSite', 'archiveJobSite']),
    // ====================
    // Navigation
    // ====================
    onCloseWizard() {
      this.$emit('close');
    },
    // Because we're using `vee-validate`, we need to use a method to
    // check whether the next button should be disabled by passing
    // the observer's `fields` object.
    nextButtonDisabled(fields = {}) {
      const { jobSiteName = {}, jobSiteAddress = {} } = fields;

      return !jobSiteName.valid || !jobSiteAddress.valid;
    },
    onNavigateToNext() {
      this.currentStep += 1;
    },
    onNavigateToPrevious() {
      this.currentStep -= 1;
    },
    onChangeTab(tabId) {
      const index = Number(tabId.replace('tab-', ''));

      if (index !== this.currentStep) {
        if (index === 0) {
          this.onNavigateToPrevious();
        } else {
          this.onNavigateToNext();
        }
      }
    },

    // ====================
    // Job Site Actions
    // ====================
    async onDeleteJobSite() {
      if (confirm('Are you sure you want to delete this job site?')) {
        try {
          this.loading = true;
          await this.archiveJobSite({ jobSiteId: this.wizardJobSite.id });
          this.$appMessage('Job site deleted');
          // This will trigger a clean-up of the "steps" internal state
          this.forcedCleanup += 1;
          this.onCloseWizard(true);
        } catch (error) {
          this.$appMessage(error && error.message);
        } finally {
          this.loading = false;
        }
      }
    },
    async onSaveJobSite() {
      // We need to clone the object to avoid mutating the original
      // and get errors from the props validators of the steps.
      const jobSitePayload = cloneDeep(this.wizardJobSite);

      try {
        this.loading = true;
        if (this.editing) {
          await this.updateJobSite({ updatedJobSite: jobSitePayload });
          this.$appMessage('Job site updated');
        } else {
          await this.createJobSite({ newJobSite: jobSitePayload });
          this.$appMessage('Job site created');
        }
        // This will trigger a clean-up of the "steps" internal state
        this.forcedCleanup += 1;
        this.onCloseWizard(true);
      } catch (error) {
        if (typeof error === 'string') {
          this.$appMessage(error);
        } else if (Array.isArray(error)) {
          this.$appMessage(error[0]);
        } else if (isObject(error)) {
          Object.entries(error).forEach(([key, errorMessage]) => {
            if (Array.isArray(errorMessage)) {
              errorMessage.forEach((message) => this.$appMessage(`${key}: ${message}`));
            } else {
              this.$appMessage(`${key}: ${errorMessage}`);
            }
          });
        } else {
          this.$appMessage(error && error.message);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
